import { ParkingSpot, UserLocation } from './IMapService'

export interface ParkingDetail {
  name?: string
  address?: string
  capacity?: string
  access?: string
  surface?: string
  covered?: boolean
  lit?: boolean
  surveillance?: string
  maxstay?: string
  operator?: string
  openingHoursRaw?: string
  feeConditional?: string
  fee?: string
  parkingType?: string
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY!
const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/search-parkings`

export class OverpassService {
  private detailCache = new Map<string, { detail: ParkingDetail; timestamp: number }>()
  private readonly DETAIL_CACHE_DURATION = 600_000 // 10 min

  // ─── Recherche via Supabase Edge Function avec fallback Overpass ───

  private normalizeLng(lng: number): number {
    while (lng > 180) lng -= 360
    while (lng < -180) lng += 360
    return lng
  }

  async searchParkings(location: UserLocation, radius: number = 2000): Promise<ParkingSpot[]> {
    // Normaliser la longitude (Leaflet peut retourner des valeurs > 180 ou < -180)
    location = { ...location, longitude: this.normalizeLng(location.longitude) }
    const r = Math.round(Math.min(radius, 2000))
    console.log(`[Parking] Searching at ${location.latitude.toFixed(4)},${location.longitude.toFixed(4)} r=${r}m`)

    // 1. Toujours essayer Supabase en premier
    try {
      console.log('[Parking] Trying Supabase Edge Function...')
      const spots = await this.searchViaSupabase(location, radius)
      console.log(`[Parking] Supabase returned ${spots.length} spots`)
      if (spots.length > 0) return spots
    } catch (error) {
      console.warn('[Parking] Supabase failed:', error)
    }

    // 2. Supabase a retourné 0 ou a échoué → essayer Overpass
    try {
      console.log('[Parking] Trying Overpass fallback...')
      const spots = await this.searchViaOverpass(location, radius)
      console.log(`[Parking] Overpass returned ${spots.length} spots`)
      if (spots.length > 0) return spots
    } catch (error) {
      console.warn('[Parking] Overpass fallback failed:', error)
    }

    // 3. Les deux n'ont rien → retourner vide
    console.log('[Parking] No results from either source')
    return []
  }

  private async searchViaSupabase(location: UserLocation, radius: number): Promise<ParkingSpot[]> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15_000)

    try {
      const response = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          lat: location.latitude,
          lng: location.longitude,
          radius: Math.round(radius),
        }),
        signal: controller.signal,
      })

      clearTimeout(timeout)

      if (!response.ok) {
        throw new Error(`Edge function error: ${response.status}`)
      }

      const results = await response.json()
      return this.mapResults(results)
    } catch (error: any) {
      clearTimeout(timeout)
      throw error
    }
  }

  private async searchViaOverpass(location: UserLocation, radius: number): Promise<ParkingSpot[]> {
    const { latitude, longitude } = location
    const r = Math.round(Math.min(radius, 2000))
    const query = `[out:json][timeout:10];(node["amenity"="parking"](around:${r},${latitude},${longitude});way["amenity"="parking"](around:${r},${latitude},${longitude});relation["amenity"="parking"](around:${r},${latitude},${longitude}););out center tags;`

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 12_000)

    try {
      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(query)}`,
        signal: controller.signal,
      })

      clearTimeout(timeout)

      if (!response.ok) throw new Error(`Overpass error: ${response.status}`)

      const data = await response.json()
      const elements = data.elements || []
      const seen = new Set<string>()

      const results = elements
        .filter((el: any) => {
          const uid = `${el.type}_${el.id}`
          if (seen.has(uid)) return false
          seen.add(uid)
          return true
        })
        .map((el: any) => {
          let lat: number, lon: number
          if (el.type === 'node') { lat = el.lat; lon = el.lon }
          else if (el.center) { lat = el.center.lat; lon = el.center.lon }
          else return null

          return {
            id: `osm_${el.type}_${el.id}`,
            latitude: lat,
            longitude: lon,
            distance_m: this.calculateDistance(latitude, longitude, lat, lon),
            tags: el.tags || {},
          }
        })
        .filter(Boolean)

      return this.mapResults(results)
    } catch (error: any) {
      clearTimeout(timeout)
      throw error
    }
  }

  private mapResults(results: any[]): ParkingSpot[] {
    return results.map((r: any) => {
      const tags = r.tags || {}
      const name = tags.name || tags['name:en'] || tags['name:fr'] || this.inferName(tags)

      this.detailCache.set(r.id, {
        detail: this.extractDetail(tags),
        timestamp: Date.now(),
      })

      return {
        place_id: r.id,
        name,
        address: this.buildAddress(tags, r.latitude, r.longitude),
        latitude: r.latitude,
        longitude: r.longitude,
        distance: Math.round(r.distance_m),
        price_level: this.inferPriceLevel(tags),
        opening_hours: tags.opening_hours ? { open_now: true, weekday_text: [tags.opening_hours] } : undefined,
      } as ParkingSpot
    })
  }

  // ─── Détails (depuis le cache pré-rempli) ───

  async fetchParkingDetail(placeId: string): Promise<ParkingDetail | null> {
    const cached = this.detailCache.get(placeId)
    if (cached && Date.now() - cached.timestamp < this.DETAIL_CACHE_DURATION) {
      return cached.detail
    }
    return null
  }

  // ─── Helpers ───

  private inferName(tags: any): string {
    if (tags.parking === 'surface') return 'Surface Parking'
    if (tags.parking === 'underground') return 'Underground Parking'
    if (tags.parking === 'multi-storey') return 'Multi-storey Parking'
    if (tags.parking === 'street_side') return 'Street Parking'
    if (tags.operator) return `${tags.operator} Parking`
    return 'Parking'
  }

  private inferPriceLevel(tags: any): number {
    if (tags.fee === 'no' || tags.fee === 'false' || tags.fee === '0') return 1
    if (tags.fee === 'yes' || tags.fee === 'true') {
      if (tags['fee:conditional'] || tags.maxstay) return 2
      if (tags.operator?.toLowerCase().includes('city') || tags.operator?.toLowerCase().includes('public')) return 2
      return 3
    }
    if (tags.parking === 'street_side') return 2
    return 2
  }

  private extractDetail(tags: any): ParkingDetail {
    return {
      name: tags.name || tags['name:en'] || tags['name:fr'],
      address: this.buildAddressFromTags(tags),
      capacity: tags.capacity,
      access: tags.access && tags.access !== 'yes' ? tags.access : undefined,
      surface: tags.surface,
      covered: tags.covered === 'yes',
      lit: tags.lit === 'yes',
      surveillance: tags.surveillance,
      maxstay: tags.maxstay,
      operator: tags.operator,
      openingHoursRaw: tags.opening_hours,
      feeConditional: tags['fee:conditional'],
      fee: tags.fee,
      parkingType: tags.parking,
    }
  }

  private buildAddress(tags: any, lat: number, lon: number): string {
    return this.buildAddressFromTags(tags) || `${lat.toFixed(4)}, ${lon.toFixed(4)}`
  }

  private buildAddressFromTags(tags: any): string {
    const parts = []
    if (tags['addr:housenumber']) parts.push(tags['addr:housenumber'])
    if (tags['addr:street']) parts.push(tags['addr:street'])
    if (tags['addr:city']) parts.push(tags['addr:city'])
    if (tags['addr:postcode']) parts.push(tags['addr:postcode'])
    return parts.join(', ')
  }

  // Gardé pour compatibilité avec OpenStreetMapService
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371
    const dLat = this.deg2rad(lat2 - lat1)
    const dLon = this.deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return Math.round(R * c * 1000)
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180)
  }
}

export const overpassService = new OverpassService()
